import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { iconSize } from '../../../../../constants/icon.constants';
import { googleClickCtaEvent } from '../../../../../utils/analytics';
import { appendRootToInternalLink } from '../../../../../utils/appendRootToInternalLink.utils';
import Icon from '../../../../@shared/icon';
import RoutableNotEditableLink from '../../../../@shared/routableNotEditableLink';
import { THeaderProps } from '../../../types/index.types';
import InputBox from './inputBox';
import TextComponentDecider from '../../../../@shared/TextComponentDecider';
import { getTextType } from '../../../../../utils/getTextType.utils';
import { eyebrowKeySeriesSpecifications, titleKeySeriesSpecifications } from '../../../../../constants/dictionary.constants';

const Header: FC<THeaderProps> = (props) => {
  const { t } = useTranslation();
  const {
    backgroundColor,
    eyebrow,
    title,
    button1,
    convertToExcelLink,
    isDesktop,
    iconsList,
    toggleAllRows,
    categoryRows,
    expandedRows,
    searchTerm,
    setSearchTerm,
    getSearchResultCount,
    printTable,
    hideArrowButton,
    moveGridLeft,
    moveGridRight,
  } = props;

  return (
    <div className="model-details-specification-table-header" style={{ backgroundColor: backgroundColor || '#f4f4f4' }}>
      <div className="model-details-specification-table-header__wrapper">
          <TextComponentDecider
            type={getTextType('Text', !eyebrow?.value)}
            tag={'p'}
            field={{ value: eyebrow?.value }}
            className="model-details-specification-table-header__eyebrow"
            dictionaryKey={eyebrowKeySeriesSpecifications}
          />
          <TextComponentDecider
            type={getTextType('Text', !title?.value)}
            tag={'h2'}
            field={{ value: title?.value }}
            className="model-details-specification-table-header__title"
            dictionaryKey={titleKeySeriesSpecifications}
          />
        <div className="model-details-specification-table-header__title-link-wrapper">
          {button1?.label && button1?.url && (
            <div className={'model-details-specification-table-header__link-cta'}>
              <RoutableNotEditableLink
                href={appendRootToInternalLink(button1.url)}
                onClick={(): void => {
                  googleClickCtaEvent('Link', button1?.label, title?.value);
                }}
              >
                {button1.label}
              </RoutableNotEditableLink>
              <Icon iconName="arrow-simple-right" size={iconSize.dsk} color={'primary'} extraClassName="arrow-icon" />
            </div>
          )}
          <div className="model-details-specification-table-header__links-wrapper model-details-specification-table-header__flex">
            {isDesktop && (
              // special case to not use RoutableNotEditableLink
              // ignored to allow download
              <a
                className="model-details-specification-table-header__link--export"
                href={convertToExcelLink}
                onClick={(): void => googleClickCtaEvent('Convert to Excel', t('cnhi-excelExport'), title?.value)}
              >
                <span className="model-details-specification-table-header__link--export__text">{t('cnhi-excelExport')}</span>
                <Icon
                  iconField={iconsList[0]}
                  color="primary"
                  size={iconSize.mldsk}
                  extraClassName="model-details-specification-table-header__link__icon"
                />
              </a>
            )}
            {isDesktop && (
              <span
                className="model-details-specification-table-header__link--print"
                onClick={() => {
                  printTable();
                  googleClickCtaEvent('Print', t('cnhi-print'), title?.value);
                }}
              >
                <span className="model-details-specification-table-header__link--print__text">{t('cnhi-print')}</span>
                <Icon
                  iconField={iconsList[1]}
                  color="primary"
                  size={iconSize.sm}
                  extraClassName="model-details-specification-table-header__link--print__icon"
                />
              </span>
            )}
          </div>
        </div>
        <div className="model-details-specification-table-header__last-row model-details-specification-table-header__flex">
          <div>
            <button className="model-details-specification-table-header__link--button" onClick={toggleAllRows}>
              {categoryRows && (
                <>
                  {expandedRows.length === categoryRows.length ? t('cnhi-collapseAll') : t('cnhi-expandAll')}
                  <Icon
                    iconName={expandedRows.length === categoryRows.length ? 'action-minus' : 'action-plus'}
                    size={iconSize.dsk}
                    color="primary"
                    extraClassName="table-expand-icon"
                  />
                </>
              )}
            </button>
            {isDesktop && <InputBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} t={t} searchResultCount={getSearchResultCount} />}
          </div>
          {!isDesktop && (
            <span className="model-details-specification-table-header__link--print">
              <span onClick={printTable}>
                <Icon
                  iconField={iconsList[1]}
                  color="primary"
                  size={iconSize.mbl}
                  extraClassName="model-details-specification-table-header__link--print__icon"
                />
              </span>
            </span>
          )}
          {!hideArrowButton && (
            <div className="model-details-specification-table-header__arrow-wrapper">
              <div className="model-details-specification-table-header__arrow" onClick={moveGridLeft}>
                <Icon
                  iconName="arrow-chevron-left"
                  size={iconSize.lg}
                  color="primary"
                  extraClassName="model-details-specification-table-header__arrow--left"
                />
              </div>
              <div className="model-details-specification-table-header__arrow" onClick={moveGridRight}>
                <Icon
                  iconName="arrow-chevron-right"
                  size={iconSize.lg}
                  color="primary"
                  extraClassName="model-details-specification-table-header__arrow--right"
                />
              </div>
            </div>
          )}
        </div>
        {!isDesktop && <InputBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} t={t} searchResultCount={getSearchResultCount} />}
      </div>
    </div>
  );
};

export default Header;
